import React, { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import { alpha, useTheme } from '@mui/material/styles';
import { yellow } from '@mui/material/colors';
import { grey } from 'src/theme/core';

type Slide = {
  component: React.ReactNode;
  backgroundImage?: string;
  scrollOnLearnMore?: boolean;
  learnMoreContent?: string;
};

interface CarouselProps {
  slides: Slide[];
}

const Carousel: React.FC<CarouselProps> = ({ slides }) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ axis: 'y', loop: false });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const theme = useTheme();

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on('select', onSelect);
  }, [emblaApi, onSelect]);

  return (
    <Box
      position="relative"
      width="100%"
      height="100%"
      display="flex"
      justifyContent="center"
      alignItems="center"
      overflow="hidden"
    >
      {/* Embla Carousel Container */}
      <Box
        ref={emblaRef}
        height="100%"
        width="100%"
        display="flex"
        flexDirection="column"
        overflow="hidden"
      >
        <Box display="flex" flexDirection="column" height="100%">
          {slides.map((slide, index) => (
            <Box
              key={index}
              flex="0 0 100%"
              height="100%"
              width="100%"
              display="flex"
              justifyContent="center"
              alignItems="center"
              position="relative"
              sx={{
                backgroundImage: slide.backgroundImage
                  ? `url(${slide.backgroundImage})`
                  : 'transparent',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            >
              {/* Slide content */}
              <Box zIndex={1} p={2}>
                {slide.component}
                {slide.scrollOnLearnMore && (
                  <Box onClick={scrollNext} sx={{ cursor: 'pointer' }}>
                    {' '}
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: '700',
                        color: '#FFD400',
                        justifyContent: 'center',
                      }}
                    >
                      {slide.learnMoreContent}{' '}
                      <Icon icon="line-md:chevron-triple-down" height={24} />
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Navigation controls (Up/Down) - floating and side by side */}
      <Box
        position="absolute"
        borderRadius="8px"
        bottom={10}
        right={10}
        display="flex"
        zIndex={2}
        bgcolor={theme.palette.background.paper}
      >
        <IconButton onClick={scrollPrev} size="small">
          <Icon
            icon="material-symbols:keyboard-arrow-up"
            height={24}
            color={theme.palette.text.primary}
          />
        </IconButton>
        <IconButton onClick={scrollNext} size="small">
          <Icon
            icon="material-symbols:keyboard-arrow-down"
            height={24}
            color={theme.palette.text.primary}
          />
        </IconButton>
      </Box>

      {/* Dots Navigation */}
      <Stack direction="row" position="absolute" top={10} right={10} zIndex={3}>
        {slides.map((_, index) => (
          <Button
            key={index}
            onClick={() => emblaApi && emblaApi.scrollTo(index)}
            sx={{
              minWidth: 'auto',
              padding: 0,
              margin: '0 !important',
            }}
          >
            <Icon
              icon="radix-icons:dot-filled"
              height={32}
              color={index === selectedIndex ? '#FFD400' : grey[600]}
            />
          </Button>
        ))}
      </Stack>
    </Box>
  );
};

export default Carousel;
